// Shared Birtvy image type + insert geometry. The insert box mirrors the product's
// computeImageInsertBox (utils/imageElement.ts): natural aspect, longest side clamped to 640 world px.
// Birtvy images use NUMBER ids (idRef counter), unlike the product's string ids, and their src is a
// local object/data URL (or a remote URL when loaded from a real deck's canvasImages).

/** Longest side an inserted/pasted image is clamped to — the product's IMAGE_ELEMENT_MAX_SIZE. */
const IMAGE_ELEMENT_MAX_SIZE = 640

/** dataTransfer MIME for dragging an image FROM the Media panel onto the canvas (Birtvy's local answer to
 *  the product's CANVAS_IMAGE_DRAG_MIME). Carries the image src string; the canvas drop places it at the
 *  drop point's world coords. */
export const BIRTVY_IMAGE_DRAG_MIME = 'application/x-birtvy-image-src'

/** Fallback box for images with no intrinsic size — the product's IMAGE_FALLBACK_SIZE. */
const IMAGE_FALLBACK_SIZE = { width: 640, height: 360 } as const

export interface BirtvyImage {
  id: number
  x: number
  y: number
  width: number
  height: number
  /** Image source — an object/data URL locally (Birtvy's local Media pool / paste), or a remote URL. */
  src: string
  /** Mixed-type canvas group membership (birtvyGroup.ts) — null/undefined = ungrouped. */
  groupId?: number | null
  /**
   * WHAT this picture is, in the words it was fetched by: "Figma", "react.dev", "solar panels at sunrise".
   * Set by placeCanvasImages, which is the only place that ever knows.
   *
   * THE BUG THIS EXISTS FOR (seen in production): six logos were wired into one slide and the generator
   * described every one of them to itself as "Image the user wired into this slide", because that string
   * was hardcoded for the whole catalog. Six anonymous pictures, six brand names in the copy, and no way
   * to tell which was which — so the slide came back with the Linear mark next to "Figma" and the Notion
   * mark next to "React Router". Nothing downstream could fix that, because the identity was thrown away
   * at fetch time and never written down.
   */
  label?: string
  /** The page this picture was extracted FROM — stamped by a web card's "Extract images". Provenance only:
   *  it is what lets a later pass tell a site's own imagery apart from an upload or a generated picture. */
  sourceUrl?: string
}

/** Insert box for a natural pixel size — aspect preserved, longest side ≤ 640 (never upscaled), the
 *  product's computeImageInsertBox sizing minus its slide-centred x/y (Birtvy places at view centre). */
export function imageInsertSize(naturalWidth: number, naturalHeight: number): { width: number; height: number } {
  const safeW = naturalWidth > 0 ? naturalWidth : IMAGE_FALLBACK_SIZE.width
  const safeH = naturalHeight > 0 ? naturalHeight : IMAGE_FALLBACK_SIZE.height
  const scale = Math.min(1, IMAGE_ELEMENT_MAX_SIZE / Math.max(safeW, safeH))
  return { width: Math.max(8, Math.round(safeW * scale)), height: Math.max(8, Math.round(safeH * scale)) }
}

/** Resolve a source's natural pixel size (object URL / data URL / remote URL). Never rejects — a source
 *  that fails to load or reports 0×0 falls back to the product's 640×360 default box. */
export function measureImage(src: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => {
      if (img.naturalWidth > 0 && img.naturalHeight > 0) resolve({ width: img.naturalWidth, height: img.naturalHeight })
      else resolve({ ...IMAGE_FALLBACK_SIZE })
    }
    img.onerror = () => resolve({ ...IMAGE_FALLBACK_SIZE })
    img.src = src
  })
}

/** File → data URL. Object URLs die on reload (blank cards after refresh); data URLs persist in Birtvy's
 *  blob like everything else. The real fix later is the product's pool upload → hosted URL. */
export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(String(r.result))
    r.onerror = () => reject(r.error)
    r.readAsDataURL(file)
  })
}
